import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Typography, Paper, Button, Stack, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { authenticationService } from 'src/services/authentication.service';
import { get } from 'src/services/httpClient.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
import { formatCurrency } from 'src/common/utils/currency';
dayjs.locale('en-gb');

// Define columns for the report download service - UPDATED: Removed userId, added customerId
const columns = [
  { field: 'id', headerName: 'ID', width: 80 },

  {
    field: 'broadcasterName',
    headerName: 'Broadcaster Name',
    minWidth: 200,
  },

  {
    field: 'packageId',
    headerName: 'Package ID',
    minWidth: 120,
  },

  {
    field: 'packageName',
    headerName: 'Package Name',
    minWidth: 220,
  },

  {
    field: 'packageType',
    headerName: 'Package Type',
    minWidth: 140,
  },

  {
    field: 'channelId',
    headerName: 'Channel ID',
    minWidth: 150,
  },

  {
    field: 'channelName',
    headerName: 'Channel Name',
    minWidth: 220,
  },

  {
    field: 'oldPrice',
    headerName: 'Old Price',
    minWidth: 110,
    type: 'number',
    valueFormatter: (params) => params.value != null ? formatCurrency(params.value) : ''
  },

  {
    field: 'newPrice',
    headerName: 'New Price',
    minWidth: 110,
    type: 'number',
    valueFormatter: (params) => params.value != null ? formatCurrency(params.value) : '',
  },

  // {
  //   field: 'vendorId',
  //   headerName: 'Vendor ID',
  //   minWidth: 160,
  // },

  {
    field: 'modifiedDate',
    headerName: 'Modified Date',
    minWidth: 180,
    valueFormatter: (params) =>
      params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm:ss') : '',
  },

  {
    field: 'doneBy',
    headerName: 'Done By',
    minWidth: 180,
  },
];

const PriceChangeHistory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [modifiedDate, setModifiedDate] = useState<Dayjs | null>(dayjs());
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();
  const [broadcasters, setBroadcasters] = useState<any[]>([]);
  const [selectedBroadcaster, setSelectedBroadcaster] = useState<string>('');
  const [subscriberStats, setSubscriberStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
  });

  const [broadcasterId, setBroadcasterId] = useState('');

const [page, setPage] = useState(0);

const [rowCount, setRowCount] = useState(0);


// Function to fetch broadcasters
  const fetchBroadcasters = async () => {
    try {
      const data = await get(`${process.env.REACT_APP_BASE_API_URL}${process.env.REACT_APP_BROADCASTER}/${vendorId}`);
      const response = data?.data
      if (Array.isArray(response)) {
        setBroadcasters(response);
      } else {
        console.error('Invalid data format for broadcasters:', response);
      }
    } catch (error) {
      console.error('Error fetching broadcasters:', error);
    }
  };

  useEffect(() => {
    fetchBroadcasters();
    const fetchData = async () => {
      

      setLoading(true);
      try {
        const date = modifiedDate
  ? modifiedDate.format("YYYY-MM-DD")
  : "";

const data = await get(
   `${process.env.REACT_APP_BASE_API_URL}/api/price-history/vendor/${vendorId}/priceHistoryReport` +
  `?vendorId=${vendorId}` +
  `&broadcasterId=${encodeURIComponent(selectedBroadcaster)}` +
  `&modifiedDate=${date}`
);
console.log('response',data)
const response = data
        const dataWithId = response?.content.map((item: any, index: number) => ({
  id: item.id ?? index + 1,
  broadcasterName: item.broadcasterName,
  packageType: item.packageType,
  packageId: item.packageId,
  packageName: item.packageName,
  channelId: item.channelId,
  channelName: item.channelName,
  oldPrice: item.oldPrice,
  newPrice: item.newPrice,
  vendorId: item.vendorId,
  modifiedDate: item.modifiedDate,
  doneBy: item.doneBy,
}));
        setRows(dataWithId);
        
        // Calculate stats
        const active = dataWithId.filter((item: any) => item.user_status === 'ACTIVE').length;
        const suspended = dataWithId.filter((item: any) => item.user_status === 'SUSPENDED').length;
        
        setSubscriberStats({
          total: dataWithId.length,
          active,
          suspended
        });
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedBroadcaster,
  modifiedDate,
  page,
  pageSize]);

  // Generate timestamped filename for Excel only
  const generateTimestampedFilename = (
    baseName: string,
    
  ): string => {
    
    const time = dayjs().format('HH-mm-ss');
    return `${baseName}`;
  };

  // Keep your original Excel logic with loading state - UPDATED with customerId
  const handleDownloadExcel = async () => {
   

    setDownloading(prev => ({ ...prev, excel: true }));
    
    try {
      
      // Prepare headers - REMOVED User Id, ADDED Customer ID
      const headers = [
  'ID',
  'Broadcaster Name',
  'Package ID',
  'Package Name',
  'Package Type',
  'Channel ID',
  'Channel Name',
  'Old Price',
  'New Price',
  'Modified Date',
  'Done By',
];
      // Prepare data rows from filtered UI data
      const data = filteredRows.map((row: any) => [
  row.id,
  row.broadcasterName,
  row.packageId,
  row.packageName,
  row.packageType,
  row.channelId,
  row.channelName,
  row.oldPrice,
  row.newPrice,
  row.modifiedDate
    ? dayjs(row.modifiedDate).format('DD/MM/YYYY HH:mm:ss')
    : '',
  row.doneBy,
]);

      // Combine headers and rows
      const finalData = [headers, ...data];

      // Create worksheet and workbook
      const worksheet = XLSX.utils.aoa_to_sheet(finalData);
      const workbook = XLSX.utils.book_new();

      // Add worksheet with a safe name
      const sheetName = 'Price_Change_History';
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Generate Excel buffer and save
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const filename = generateTimestampedFilename('Price_Change_History') + '.xlsx';
      saveAs(blob, filename);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(prev => ({ ...prev, excel: false }));
    }
  };

  // Single download handler for PDF using common service - UPDATED with customerId
  const handleDownloadPDF = async () => {
    

    setDownloading(prev => ({ ...prev, pdf: true }));
    
    try {
      
      const searchFilterText = searchTerm || 'No Search Filter';
      
      // Generate custom filename
     const customFileName =
`Price_Change_History_${dayjs().format('HHmmss')}.pdf`;
      
      // Update PDF columns - remove userId, add customerId
     const pdfColumns = [
  { field: 'id', headerName: 'ID' },
  { field: 'packageType', headerName: 'Package Type' },
  
  { field: 'broadcasterName', headerName: 'Broadcaster Name' },
  { field: 'packageId', headerName: 'Package ID' },
  { field: 'packageName', headerName: 'Package Name' },
  { field: 'channelId', headerName: 'Channel ID' },
  { field: 'channelName', headerName: 'Channel Name' },
  { field: 'oldPrice', headerName: 'Old Price' },
  { field: 'newPrice', headerName: 'New Price' },
  { field: 'vendorId', headerName: 'Vendor ID' },
  { field: 'modifiedDate', headerName: 'Modified Date' },
  { field: 'doneBy', headerName: 'Done By' },
];

      const success = await reportDownloadService.downloadReport({
        title: 'Price Change History',
        columns: pdfColumns,
        rows: filteredRows,
        
        fileName: customFileName,
      }, 'pdf');
      
      if (!success) {
        alert('Failed to generate PDF. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, pdf: false }));
    }
  };

  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <>
      

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="Price Change Historys"
               subHeading="View package and channel price modification history"
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasPermission(["OWNER"]) && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadExcel}
                  disabled={downloading.excel}
                >
                  {downloading.excel ? 'Generating...' : 'Download Excel'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={downloading.pdf}
                >
                  {downloading.pdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>

      {/* STATS SECTION - Commented out */}
      {/* <Box sx={{ px: 2, pt: 0, pb: 2 }}>
        <Paper elevation={4} sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'space-between' }}>
          <Typography variant="subtitle1">Total Subscribers: <strong>{subscriberStats.total}</strong></Typography>
          <Typography variant="subtitle1">Active Subscribers: <strong>{subscriberStats.active}</strong></Typography>
          <Typography variant="subtitle1">Suspended Subscribers: <strong>{subscriberStats.suspended}</strong></Typography>
        </Paper>
      </Box> */}

      {/* FILTER SECTION */}
      <Box sx={{ px: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
  <Stack
    direction={{ xs: "column", md: "row" }}
    spacing={2}
    alignItems="center"
  >
    <TextField
      label="Search"
      size="small"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      sx={{ minWidth: 220 }}
    />

    <FormControl size="small"  sx={{width:'300px'}}>
        <InputLabel>Broadcaster</InputLabel>
        <Select
          value={selectedBroadcaster}
          label="Broadcaster"
          onChange={(e) => setSelectedBroadcaster(e.target.value)}
        >
          <MenuItem value="">All Broadcasters</MenuItem>
          {broadcasters.map((broadcaster) => (
            <MenuItem key={broadcaster.id} value={broadcaster.id}>
              {broadcaster.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    {/* <TextField
      label="Broadcaster ID"
      size="small"
      value={broadcasterId}
      onChange={(e) => setBroadcasterId(e.target.value)}
      sx={{ minWidth: 220 }}
    /> */}

    <DatePicker
      label="Modified Date"
      value={modifiedDate}
      onChange={(newValue) => setModifiedDate(newValue)}
      format="DD/MM/YYYY"
      slotProps={{
        textField: {
          size: "small",
          sx: { minWidth: 180 }
        }
      }}
    />
  </Stack>
</LocalizationProvider>
      </Box>

      {/* DATAGRID SECTION */}
      <Box sx={{ height: 'calc(100vh - 280px)', width: '100%', padding: '15px' }}>
        <DataGrid
  rows={rows}
  columns={columns}
  rowCount={rowCount}
  pagination
  paginationMode="server"
  page={page}
  pageSize={pageSize}
  onPageChange={(newPage) => setPage(newPage)}
  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
  rowsPerPageOptions={[20, 50, 100]}
/>
      </Box>
    </>
  );
};

export default PriceChangeHistory;
